import { useCurrentWorkspace } from '@cube3/state/src/redux/components/Administration/withCurrentWorkspace';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import { useMemo } from 'react';
import { removeDiacritics as removeDiacriticsHelper } from '../../Exports/utils/broadcastFieldValidators';
import { useAccountSuggestions } from './useAccountSuggestions';

/** helper functions */
const matchInputValue = (pool: string[], inputValue: string = '') => {
  return pool.some((item) => item.includes(removeDiacriticsHelper(inputValue)));
};
const removeDiacritics = (str: string = '') => {
  return removeDiacriticsHelper(str.toLocaleLowerCase());
};

export const useProjectMembersSuggestions = (
  projectId: string,
  excludedAccounts?: string[]
) => {
  const [workspaceId] = useCurrentWorkspace();

  const {
    suggestedAccounts,
    excludedAccounts: excludedSuggestion,
    query,
    setQuery,
    loading
  } = useAccountSuggestions(excludedAccounts);

  const { resources: allTeams, loading: allTeamsLoading } =
    useResourceList__ALPHA({
      resourceType: 'workspace',
      resourceId: workspaceId,
      edgeType: 'team',
      edgeLabel: 'teams'
    });

  const { resources: alreadyMemberTeams } = useResourceList__ALPHA({
    edgeType: 'project-team-membership',
    edgeLabel: 'project-team-memberships',
    params: {
      filter: { project: projectId }
    },
    strategy: 'fetch-on-mount'
  });

  const allSuggestions = useMemo(() => {
    const all = [].concat(allTeams, suggestedAccounts).filter(Boolean);
    const added = alreadyMemberTeams?.map((res) => res.relationships.team?.id);
    const filtered = all.filter((team) => !added.includes(team?.id));
    if (query) {
      const inputValue = query.toLocaleLowerCase().trim();
      const queried = all.filter((res) => {
        const display_name = removeDiacritics(res?.display_name);
        const full_name = removeDiacritics(res?.full_name);

        return matchInputValue([display_name, full_name], inputValue);
      });

      return queried;
    }
    return filtered;
  }, [allTeams, alreadyMemberTeams, query, suggestedAccounts]);

  const allExcluded = useMemo(() => {
    const added = alreadyMemberTeams?.map((res) => res.relationships.team?.id);
    const excludedTeams = allTeams.filter((team) => added.includes(team.id));

    return [].concat(excludedSuggestion, excludedTeams);
  }, [alreadyMemberTeams, allTeams, excludedSuggestion]);

  return {
    allSuggestions,
    allExcluded,
    query,
    setQuery,
    loading: allTeamsLoading || loading
  };
};
